import React, { useEffect, useState } from 'react';
import { Box, Title, Text, Card, Group, Badge, Button, SimpleGrid, Divider, ActionIcon, Modal, Tabs, ThemeIcon } from '@mantine/core';
import { Bookmark, Trash, Eye, LineChart, Puzzle, Compass, Star, Apple, Plus, Minus, Equal, ExternalLink } from 'lucide-react';
import { ProtocolRadarChart } from './ProtocolRadarChart';
// Interface for saved vectors
interface SavedVector {
  id: string;
  name: string;
  protocol: any; // The full protocol object
  savedAt: string;
}
// Interface for saved builds
interface SavedBuild {
  id: string;
  name: string;
  selectedOutcomes: string[];
  combinations: any[]; // The compatible combinations
  savedAt: string;
}
// Interface for saved explore terms
interface SavedExplore {
  id: string;
  term: string;
  tag: string;
  category: string;
  summary: string;
  link: string;
  positive?: string[];
  neutral?: string[];
  negative?: string[];
  savedAt: string;
}
export function SavedVectors() {
  // State for different types of saved items
  const [savedVectors, setSavedVectors] = useState<SavedVector[]>([]);
  const [savedBuilds, setSavedBuilds] = useState<SavedBuild[]>([]);
  const [savedExplores, setSavedExplores] = useState<SavedExplore[]>([]);
  // State for selected items and modals
  const [selectedVector, setSelectedVector] = useState<SavedVector | null>(null);
  const [selectedBuild, setSelectedBuild] = useState<SavedBuild | null>(null);
  const [selectedExplore, setSelectedExplore] = useState<SavedExplore | null>(null);
  const [viewVectorModalOpen, setViewVectorModalOpen] = useState(false);
  const [viewBuildModalOpen, setViewBuildModalOpen] = useState(false);
  const [viewExploreModalOpen, setViewExploreModalOpen] = useState(false);
  // Current active tab
  const [activeTab, setActiveTab] = useState<string | null>('vectors');
  // Load saved items from localStorage on component mount
  useEffect(() => {
    // Load vectors
    const vectors = localStorage.getItem('nutrivector-saved');
    if (vectors) {
      setSavedVectors(JSON.parse(vectors));
    }
    // Load builds
    const builds = localStorage.getItem('nutrivector-saved-builds');
    if (builds) {
      setSavedBuilds(JSON.parse(builds));
    }
    // Load explores
    const explores = localStorage.getItem('nutrivector-saved-explores');
    if (explores) {
      setSavedExplores(JSON.parse(explores));
    }
  }, []);
  // Delete handlers for each type
  const handleDeleteVector = (id: string) => {
    const updatedVectors = savedVectors.filter(vector => vector.id !== id);
    setSavedVectors(updatedVectors);
    localStorage.setItem('nutrivector-saved', JSON.stringify(updatedVectors));
  };
  const handleDeleteBuild = (id: string) => {
    const updatedBuilds = savedBuilds.filter(build => build.id !== id);
    setSavedBuilds(updatedBuilds);
    localStorage.setItem('nutrivector-saved-builds', JSON.stringify(updatedBuilds));
  };
  const handleDeleteExplore = (id: string) => {
    const updatedExplores = savedExplores.filter(explore => explore.id !== id);
    setSavedExplores(updatedExplores);
    localStorage.setItem('nutrivector-saved-explores', JSON.stringify(updatedExplores));
  };
  // View handlers for each type
  const handleViewVector = (vector: SavedVector) => {
    setSelectedVector(vector);
    setViewVectorModalOpen(true);
  };
  const handleViewBuild = (build: SavedBuild) => {
    setSelectedBuild(build);
    setViewBuildModalOpen(true);
  };
  const handleViewExplore = (explore: SavedExplore) => {
    setSelectedExplore(explore);
    setViewExploreModalOpen(true);
  };
  // Format the date string
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Render the selected vector's details in a modal
  const renderVectorDetails = () => {
    if (!selectedVector) return null;
    // Split the protocol name into its components
    let [namePart1, namePart2] = selectedVector.protocol.name.split(' | ');
    return <Modal opened={viewVectorModalOpen} onClose={() => setViewVectorModalOpen(false)} title={<Title order={4} data-id="element-355">Vector Details</Title>} size="lg" data-id="element-354">
        <div className="text-center mb-6" data-id="element-356">
          <Title order={3} className="text-2xl font-bold text-slate-800 mb-2" data-id="element-357">
            {namePart1}
          </Title>
          <Title order={4} className="text-lg font-medium text-emerald-600 mb-4" data-id="element-358">
            Fitnut: {namePart2}
          </Title>
          <Text size="sm" className="text-slate-500" data-id="element-359">
            Saved on {formatDate(selectedVector.savedAt)}
          </Text>
        </div>
        <Divider my="md" data-id="element-360" />
        <Group className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-361">
          {Object.entries(selectedVector.protocol.selections).map(([key, value]) => <Badge key={key} size="lg" color="gray" variant="outline" data-id="element-362">
                {value}
              </Badge>)}
        </Group>
        <div className="flex justify-center mb-6" data-id="element-363">
          <div className="w-full max-w-md" data-id="element-364">
            <div className="flex items-center justify-center mb-2" data-id="element-365">
              <LineChart size={18} className="text-emerald-600 mr-2" data-id="element-366" />
              <Title order={5} className="text-slate-700" data-id="element-367">
                Protocol Performance Profile
              </Title>
            </div>
            <Text size="sm" className="text-slate-500 mb-4 text-center" data-id="element-368">
              Predicted optimization across 12 health dimensions
            </Text>
            <ProtocolRadarChart data={selectedVector.protocol.radarData} height={300} data-id="element-369" />
          </div>
        </div>
        <Divider my="md" data-id="element-370" />
        <Group position="center" data-id="element-371">
          <Button variant="outline" color="red" leftSection={<Trash size={16} data-id="element-373" />} onClick={() => {
          handleDeleteVector(selectedVector.id);
          setViewVectorModalOpen(false);
        }} data-id="element-372">
            Delete Vector
          </Button>
          <Button variant="filled" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setViewVectorModalOpen(false)} data-id="element-374">
            Close
          </Button>
        </Group>
      </Modal>;
  };
  // Render the selected build's details in a modal
  const renderBuildDetails = () => {
    if (!selectedBuild) return null;
    return <Modal opened={viewBuildModalOpen} onClose={() => setViewBuildModalOpen(false)} title={<Title order={4} data-id="element-376">Build Details</Title>} size="lg" data-id="element-375">
        <div className="text-center mb-6" data-id="element-377">
          <Title order={3} className="text-2xl font-bold text-slate-800 mb-2" data-id="element-378">
            Optimized Build
          </Title>
          <Group position="center" className="mb-4" data-id="element-379">
            {selectedBuild.selectedOutcomes.map((outcome, index) => <Badge key={index} size="lg" color="emerald" variant="filled" className="bg-emerald-600" data-id="element-380">
                {outcome}
              </Badge>)}
          </Group>
          <Text size="sm" className="text-slate-500" data-id="element-381">
            Saved on {formatDate(selectedBuild.savedAt)}
          </Text>
        </div>
        <Divider my="md" data-id="element-382" />
        <Title order={4} className="mb-4 text-slate-700" data-id="element-383">
          Top Compatible Combinations
        </Title>
        {selectedBuild.combinations.slice(0, 2).map((combo, index) => {
        const [namePart1, namePart2] = combo.name.split(' | ');
        return <div key={index} className="bg-slate-50 p-4 rounded-md mb-4" data-id="element-384">
              <Group position="apart" className="mb-2" data-id="element-385">
                <Badge color="emerald" size="md" data-id="element-386">
                  Combination #{index + 1}
                </Badge>
                <Text size="sm" className="text-slate-500" data-id="element-387">
                  Score: {combo._score.toFixed(1)}/5
                </Text>
              </Group>
              <Title order={5} className="text-slate-800 mb-1" data-id="element-388">
                {namePart1}
              </Title>
              <Text className="text-emerald-600 text-sm mb-3" data-id="element-389">{namePart2}</Text>
              <div className="flex justify-center mb-2" data-id="element-390">
                <ProtocolRadarChart data={combo.radarData} height={200} data-id="element-391" />
              </div>
            </div>;
      })}
        <Divider my="md" data-id="element-392" />
        <Group position="center" data-id="element-393">
          <Button variant="outline" color="red" leftSection={<Trash size={16} data-id="element-395" />} onClick={() => {
          handleDeleteBuild(selectedBuild.id);
          setViewBuildModalOpen(false);
        }} data-id="element-394">
            Delete Build
          </Button>
          <Button variant="filled" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setViewBuildModalOpen(false)} data-id="element-396">
            Close
          </Button>
        </Group>
      </Modal>;
  };
  // Render the selected explore term's details in a modal
  const renderExploreDetails = () => {
    if (!selectedExplore) return null;
    return <Modal opened={viewExploreModalOpen} onClose={() => setViewExploreModalOpen(false)} title={<Title order={4} data-id="element-398">Term Details</Title>} size="lg" data-id="element-397">
        <div className="text-center mb-6" data-id="element-399">
          <Title order={3} className="text-2xl font-bold text-slate-800 mb-2" data-id="element-400">
            {selectedExplore.term}
          </Title>
          <Badge size="lg" color="emerald" className="mb-4" data-id="element-401">
            {selectedExplore.tag}
          </Badge>
          <Text size="sm" className="text-slate-500" data-id="element-402">
            Category: {selectedExplore.category} • Saved on{' '}
            {formatDate(selectedExplore.savedAt)}
          </Text>
        </div>
        <Divider my="md" data-id="element-403" />
        <Text className="text-slate-700 mb-6" data-id="element-404">{selectedExplore.summary}</Text>
        {selectedExplore.positive || selectedExplore.neutral || selectedExplore.negative ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-405">
            <div className="bg-emerald-50 p-4 rounded-md" data-id="element-406">
              <Group className="mb-2" data-id="element-407">
                <ThemeIcon color="green" variant="light" size="md" radius="xl" data-id="element-408">
                  <Plus size={16} data-id="element-409" />
                </ThemeIcon>
                <Text fw={600} className="text-emerald-700" data-id="element-410">
                  Positive Impact
                </Text>
              </Group>
              {selectedExplore.positive && selectedExplore.positive.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-411">
                  {selectedExplore.positive.map((item, idx) => <li key={idx} data-id="element-412">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-413">
                  No specific positive protocols listed.
                </Text>}
            </div>
            <div className="bg-slate-100 p-4 rounded-md" data-id="element-414">
              <Group className="mb-2" data-id="element-415">
                <ThemeIcon color="gray" variant="light" size="md" radius="xl" data-id="element-416">
                  <Equal size={16} data-id="element-417" />
                </ThemeIcon>
                <Text fw={600} className="text-slate-700" data-id="element-418">
                  Neutral Impact
                </Text>
              </Group>
              {selectedExplore.neutral && selectedExplore.neutral.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-419">
                  {selectedExplore.neutral.map((item, idx) => <li key={idx} data-id="element-420">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-421">
                  No specific neutral protocols listed.
                </Text>}
            </div>
            <div className="bg-red-50 p-4 rounded-md" data-id="element-422">
              <Group className="mb-2" data-id="element-423">
                <ThemeIcon color="red" variant="light" size="md" radius="xl" data-id="element-424">
                  <Minus size={16} data-id="element-425" />
                </ThemeIcon>
                <Text fw={600} className="text-red-700" data-id="element-426">
                  Negative Impact
                </Text>
              </Group>
              {selectedExplore.negative && selectedExplore.negative.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-427">
                  {selectedExplore.negative.map((item, idx) => <li key={idx} data-id="element-428">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-429">
                  No specific negative protocols listed.
                </Text>}
            </div>
          </div> : null}
        <Group position="apart" className="mt-4" data-id="element-430">
          <Button variant="outline" color="red" leftSection={<Trash size={16} data-id="element-432" />} onClick={() => {
          handleDeleteExplore(selectedExplore.id);
          setViewExploreModalOpen(false);
        }} data-id="element-431">
            Remove from Saved
          </Button>
          <Group data-id="element-433">
            <Button component="a" href={selectedExplore.link} target="_blank" rel="noopener noreferrer" variant="outline" color="emerald" rightSection={<ExternalLink size={16} data-id="element-435" />} data-id="element-434">
              Learn More
            </Button>
            <Button variant="filled" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setViewExploreModalOpen(false)} data-id="element-436">
              Close
            </Button>
          </Group>
        </Group>
      </Modal>;
  };
  // Get total count of all saved items
  const totalSavedItems = savedVectors.length + savedBuilds.length + savedExplores.length;
  return <Box className="p-8 bg-white rounded-lg shadow-sm" data-id="element-437">
      <Group position="apart" className="mb-6" data-id="element-438">
        <div data-id="element-439">
          <Title order={2} className="mb-2 text-slate-800" data-id="element-440">
            Saved Items
          </Title>
          <Text className="text-slate-600" data-id="element-441">
            Your personalized nutrition information saved for future reference
          </Text>
        </div>
        <Badge size="lg" color="emerald" className="bg-emerald-600" data-id="element-442">
          {totalSavedItems} Saved
        </Badge>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} className="mb-6" data-id="element-443">
        <Tabs.List data-id="element-444">
          <Tabs.Tab value="vectors" leftSection={<Apple size={16} data-id="element-446" />} rightSection={savedVectors.length > 0 ? <Badge size="xs" color="emerald" variant="filled" className="ml-2" data-id="element-447">
                  {savedVectors.length}
                </Badge> : null} data-id="element-445">
            Vectors
          </Tabs.Tab>
          <Tabs.Tab value="builds" leftSection={<Puzzle size={16} data-id="element-449" />} rightSection={savedBuilds.length > 0 ? <Badge size="xs" color="emerald" variant="filled" className="ml-2" data-id="element-450">
                  {savedBuilds.length}
                </Badge> : null} data-id="element-448">
            Builds
          </Tabs.Tab>
          <Tabs.Tab value="explores" leftSection={<Compass size={16} data-id="element-452" />} rightSection={savedExplores.length > 0 ? <Badge size="xs" color="emerald" variant="filled" className="ml-2" data-id="element-453">
                  {savedExplores.length}
                </Badge> : null} data-id="element-451">
            Explores
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* Vectors Tab Content */}
      {activeTab === 'vectors' && <>
          {savedVectors.length === 0 ? <Card shadow="sm" p="xl" radius="md" className="text-center bg-slate-50" data-id="element-454">
              <Bookmark size={48} className="text-slate-400 mx-auto mb-4" data-id="element-455" />
              <Title order={3} className="text-slate-700 mb-2" data-id="element-456">
                No Saved Vectors Yet
              </Title>
              <Text className="text-slate-600 mb-4" data-id="element-457">
                When you find a nutrition protocol that works for you, save it
                here for easy access.
              </Text>
              <Text size="sm" className="text-slate-500" data-id="element-458">
                Try the Vector tool to create your first personalized protocol.
              </Text>
            </Card> : <SimpleGrid cols={3} spacing="lg" breakpoints={[{
        maxWidth: 'md',
        cols: 2,
        spacing: 'md'
      }, {
        maxWidth: 'sm',
        cols: 1,
        spacing: 'sm'
      }]} data-id="element-459">
              {savedVectors.map(vector => {
          // Split the protocol name into its components
          let [namePart1, namePart2] = vector.protocol.name.split(' | ');
          return <Card key={vector.id} shadow="sm" p="lg" radius="md" className="flex flex-col" data-id="element-460">
                    <Group position="apart" className="mb-2" data-id="element-461">
                      <Badge color="emerald" size="sm" data-id="element-462">
                        {formatDate(vector.savedAt)}
                      </Badge>
                      <Group spacing="xs" data-id="element-463">
                        <ActionIcon variant="subtle" color="blue" onClick={() => handleViewVector(vector)} data-id="element-464">
                          <Eye size={16} data-id="element-465" />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteVector(vector.id)} data-id="element-466">
                          <Trash size={16} data-id="element-467" />
                        </ActionIcon>
                      </Group>
                    </Group>
                    <Title order={4} className="text-lg font-medium text-slate-800 mb-1" data-id="element-468">
                      {namePart1}
                    </Title>
                    <Text className="text-emerald-600 text-sm mb-3" data-id="element-469">
                      {namePart2}
                    </Text>
                    <div className="flex flex-wrap gap-1 mb-4" data-id="element-470">
                      {Object.values(vector.protocol.selections).slice(0, 4).map((selection, index) => <Badge key={index} size="sm" color="gray" variant="outline" data-id="element-471">
                            {selection}
                          </Badge>)}
                      {Object.values(vector.protocol.selections).length > 4 && <Badge size="sm" color="gray" variant="outline" data-id="element-472">
                          +
                          {Object.values(vector.protocol.selections).length - 4}{' '}
                          more
                        </Badge>}
                    </div>
                    <div className="mt-auto" data-id="element-473">
                      <Button fullWidth variant="light" color="emerald" onClick={() => handleViewVector(vector)} data-id="element-474">
                        View Details
                      </Button>
                    </div>
                  </Card>;
        })}
            </SimpleGrid>}
        </>}

      {/* Builds Tab Content */}
      {activeTab === 'builds' && <>
          {savedBuilds.length === 0 ? <Card shadow="sm" p="xl" radius="md" className="text-center bg-slate-50" data-id="element-475">
              <Puzzle size={48} className="text-slate-400 mx-auto mb-4" data-id="element-476" />
              <Title order={3} className="text-slate-700 mb-2" data-id="element-477">
                No Saved Builds Yet
              </Title>
              <Text className="text-slate-600 mb-4" data-id="element-478">
                Save your custom nutrition builds based on your health
                priorities.
              </Text>
              <Text size="sm" className="text-slate-500" data-id="element-479">
                Try the Build tool to create your first personalized build.
              </Text>
            </Card> : <SimpleGrid cols={3} spacing="lg" breakpoints={[{
        maxWidth: 'md',
        cols: 2,
        spacing: 'md'
      }, {
        maxWidth: 'sm',
        cols: 1,
        spacing: 'sm'
      }]} data-id="element-480">
              {savedBuilds.map(build => <Card key={build.id} shadow="sm" p="lg" radius="md" className="flex flex-col" data-id="element-481">
                  <Group position="apart" className="mb-2" data-id="element-482">
                    <Badge color="emerald" size="sm" data-id="element-483">
                      {formatDate(build.savedAt)}
                    </Badge>
                    <Group spacing="xs" data-id="element-484">
                      <ActionIcon variant="subtle" color="blue" onClick={() => handleViewBuild(build)} data-id="element-485">
                        <Eye size={16} data-id="element-486" />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteBuild(build.id)} data-id="element-487">
                        <Trash size={16} data-id="element-488" />
                      </ActionIcon>
                    </Group>
                  </Group>
                  <Title order={4} className="text-lg font-medium text-slate-800 mb-3" data-id="element-489">
                    Optimized Build
                  </Title>
                  <div className="flex flex-wrap gap-1 mb-4" data-id="element-490">
                    {build.selectedOutcomes.map((outcome, index) => <Badge key={index} size="sm" color="emerald" data-id="element-491">
                        {outcome}
                      </Badge>)}
                  </div>
                  <Text size="sm" className="text-slate-600 mb-4" data-id="element-492">
                    {build.combinations.length} compatible combinations found
                  </Text>
                  <div className="mt-auto" data-id="element-493">
                    <Button fullWidth variant="light" color="emerald" onClick={() => handleViewBuild(build)} data-id="element-494">
                      View Details
                    </Button>
                  </div>
                </Card>)}
            </SimpleGrid>}
        </>}

      {/* Explores Tab Content */}
      {activeTab === 'explores' && <>
          {savedExplores.length === 0 ? <Card shadow="sm" p="xl" radius="md" className="text-center bg-slate-50" data-id="element-495">
              <Star size={48} className="text-slate-400 mx-auto mb-4" data-id="element-496" />
              <Title order={3} className="text-slate-700 mb-2" data-id="element-497">
                No Saved Terms Yet
              </Title>
              <Text className="text-slate-600 mb-4" data-id="element-498">
                Star terms from the Explore section to save them for future
                reference.
              </Text>
              <Text size="sm" className="text-slate-500" data-id="element-499">
                Visit the Explore section to discover and save nutrition
                terminology.
              </Text>
            </Card> : <SimpleGrid cols={3} spacing="lg" breakpoints={[{
        maxWidth: 'md',
        cols: 2,
        spacing: 'md'
      }, {
        maxWidth: 'sm',
        cols: 1,
        spacing: 'sm'
      }]} data-id="element-500">
              {savedExplores.map(explore => <Card key={explore.id} shadow="sm" p="lg" radius="md" className="flex flex-col" data-id="element-501">
                  <Group position="apart" className="mb-2" data-id="element-502">
                    <Badge color="gray" size="sm" data-id="element-503">
                      {explore.category}
                    </Badge>
                    <Group spacing="xs" data-id="element-504">
                      <ActionIcon variant="subtle" color="blue" onClick={() => handleViewExplore(explore)} data-id="element-505">
                        <Eye size={16} data-id="element-506" />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteExplore(explore.id)} data-id="element-507">
                        <Trash size={16} data-id="element-508" />
                      </ActionIcon>
                    </Group>
                  </Group>
                  <Title order={4} className="text-lg font-medium text-slate-800 mb-1" data-id="element-509">
                    {explore.term}
                  </Title>
                  <Badge color="emerald" className="mb-3" data-id="element-510">
                    {explore.tag}
                  </Badge>
                  <Text size="sm" className="text-slate-600 mb-4 line-clamp-3" data-id="element-511">
                    {explore.summary}
                  </Text>
                  <div className="mt-auto" data-id="element-512">
                    <Button fullWidth variant="light" color="emerald" onClick={() => handleViewExplore(explore)} data-id="element-513">
                      View Details
                    </Button>
                  </div>
                </Card>)}
            </SimpleGrid>}
        </>}

      {/* Render modals */}
      {renderVectorDetails()}
      {renderBuildDetails()}
      {renderExploreDetails()}
    </Box>;
}